// Process-run lookups for the log viewer, issued through the SQL firewall
// (sqlFirewallQuery → dbaccess.v1.Query) rather than the qubes BiQL /api/query
// endpoint. The BiQL path used the "nefario" appSpace, which does not exist on the
// continuum backend (404); the firewall query targets the continuum processrun cube
// and its calculated/join fields (scheduleUid, job.label, event.schedule.name)
// the same way the services grid does.

import { sqlFirewallQuery } from "../qubes-table/table-model";
import { LogParserType, parseLogParserType } from "./types";
import { isLogChannel } from "./channels";

export interface ProcessRunRow {
  uid: string;
  startedAt: string;
  completedAt: string | null;
  status: string;
  success: boolean | null;
  processCompleteKind: string | null; // processComplete.kind ('awol' for a swept run); raw-jsonb derived
  job_uid: string;
  "job.label": string;
  scheduleUid: string | null;
  "event.schedule.name": string | null;
}

function esc(v: string): string {
  return v.replace(/'/g, "''");
}

// The calculated/join columns the log viewer reads off a processrun row. Quoted
// dotted aliases so the returned object keys match the ProcessRunRow shape.
//
// FULL_RUN_FIELDS pulls the schedule name and job label. BUT scheduleUid
// (= jobevent.schedule_uid), event.schedule.name, success and job.label are all
// calculated/join columns, and the firewall cannot plan the
// processrun->jobevent / ->job joins for a `WHERE uid=` lookup on every uid — it
// throws "missing FROM-clause entry for table jobevent". So we fall back to
// RAW_RUN_FIELDS (only physical processrun columns — never a join) and resolve
// the display name with a separate direct `job` lookup. This keeps the bar's
// label and run dropdown working instead of collapsing to the raw uid.
// success and processCompleteKind are calculated from RAW jsonb (exitCode / kind
// in processComplete) — no join — so they're safe in BOTH the full and raw paths
// and let the dropdown show Running/Awol/Success/Failure.
const FULL_RUN_FIELDS = `uid, startedAt, completedAt, status, success, processCompleteKind, job_uid,
  job.label AS "job.label", scheduleUid,
  event.schedule.name AS "event.schedule.name"`;
const RAW_RUN_FIELDS = `uid, startedat, completedat, status, success, processCompleteKind, job_uid`;

// Map a raw processrun row (snake_case, no joins) to ProcessRunRow, filling the
// calculated fields we couldn't fetch with safe defaults; jobName (if resolved
// via a direct job lookup) populates job.label.
function rawToRunRow(r: Record<string, unknown>, jobName?: string): ProcessRunRow {
  const row = normalizeRunRow(r);
  // The raw path has no job.label column; use the directly-resolved job name.
  if (!row["job.label"] && jobName) row["job.label"] = jobName;
  return row;
}

// normalizeRunRow maps a dbaccess result row to ProcessRunRow regardless of how
// the backend cased the columns. dbaccess lowercases identifiers, so the FULL
// query's `startedAt`/`completedAt`/`processCompleteKind` aliases and dotted
// aliases come back lower/snake-cased; read both forms so the camelCase
// ProcessRunRow keys the UI expects are always populated (otherwise dates are
// blank and every run looks "Running").
export function normalizeRunRow(r: Record<string, unknown>): ProcessRunRow {
  const started = r.startedAt ?? r.startedat ?? "";
  const completed = r.completedAt ?? r.completedat ?? null;
  const kind = r.processCompleteKind ?? r.processcompletekind ?? null;
  return {
    uid: String(r.uid ?? ""),
    startedAt: started ? String(started) : "",
    completedAt: completed != null ? String(completed) : null,
    status: String(r.status ?? ""),
    success: typeof r.success === "boolean" ? r.success : null,
    processCompleteKind: kind != null ? String(kind) : null,
    job_uid: String(r.job_uid ?? ""),
    "job.label": String(r["job.label"] ?? r.label ?? ""),
    scheduleUid: (r.scheduleUid ?? r.scheduleuid) != null ? String(r.scheduleUid ?? r.scheduleuid) : null,
    "event.schedule.name":
      (r["event.schedule.name"] ?? r.sname) != null ? String(r["event.schedule.name"] ?? r.sname) : null,
  };
}

// resolveJobName fetches a job's display name (raw `name` column — no join) so
// the bar can show it when the calculated job.label join is unavailable.
async function resolveJobName(jobUid: string): Promise<string | undefined> {
  if (!jobUid) return undefined;
  try {
    const rows = await sqlFirewallQuery(
      `SELECT name FROM job WHERE uid = '${esc(jobUid)}'`,
    );
    return rows[0]?.name ? String(rows[0].name) : undefined;
  } catch {
    return undefined;
  }
}

// Fetch a single process run by uid (used to resolve the current run's
// job/schedule identity and its display label). Tolerant of the firewall
// join-planning failure: on error it retries with raw columns only and resolves
// the job name directly, so the label/identity still resolve.
export async function fetchProcessRunByUid(
  processUid: string,
): Promise<ProcessRunRow | undefined> {
  try {
    const rows = await sqlFirewallQuery(
      `SELECT ${FULL_RUN_FIELDS} FROM processrun WHERE uid = '${esc(processUid)}'`,
    );
    return rows[0] ? normalizeRunRow(rows[0]) : undefined;
  } catch {
    const rows = await sqlFirewallQuery(
      `SELECT ${RAW_RUN_FIELDS} FROM processrun WHERE uid = '${esc(processUid)}'`,
    );
    const raw = rows[0];
    if (!raw) return undefined;
    const jobName = await resolveJobName(String(raw.job_uid ?? ""));
    return rawToRunRow(raw, jobName);
  }
}

// Cheap existence check: does a processrun row with this uid exist? Selects
// ONLY the uid (no calculated/join fields) so it can't trip the firewall's
// jobevent-join planning — unlike fetchProcessRunByUid, whose
// event.schedule.name reference can throw for some uids. Used to tell apart
// "process exists but has no logs" from "no such process run" when the log
// stream 404s. Returns true if exactly the row is present.
export async function processRunExists(processUid: string): Promise<boolean> {
  const rows = await sqlFirewallQuery(
    `SELECT uid FROM processrun WHERE uid = '${esc(processUid)}'`,
  );
  return rows.length > 0;
}

// Parse processrun.channelNames (a comma-joined string_agg) into the run's LOG
// channels. Pure — the query wrapper is below. What counts as a log channel is
// channels.ts's call, not ours (the rpc mailboxes are streams on the ledger but
// carry RPC envelopes, not logs); an UNKNOWN channel is treated as a log channel,
// so a new one shows up on its own rather than being invisible until someone
// remembers to list it.
export function parseLogChannels(raw: unknown): string[] {
  if (!raw) return [];
  return String(raw)
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c.length > 0 && isLogChannel(c));
}

// Did this run have a mesh mailbox — i.e. is there RPC traffic to look at? True when
// the ledger registered an rpc channel for it. parseLogChannels deliberately drops
// those (they carry RPC envelopes, not logs), so this reads the RAW list.
//
// Note we do NOT need the mailbox's reader key to answer this, and deliberately do not
// fetch one: /api/processrun_tail is addressed by PROCESS RUN, and the server resolves
// the mailbox itself. The browser never holds a mailbox credential.
export function hasMeshTraffic(raw: unknown): boolean {
  if (!raw) return false;
  return String(raw)
    .split(",")
    .map((c) => c.trim())
    .some((c) => c === "rpc-inbox" || c === "rpc-sent");
}

// What a run offers the viewer: its log channels, and whether it has mesh traffic.
export interface ProcessRunViews {
  channels: string[];
  /** The run had a mesh mailbox, so its correlated RPC traffic can be viewed. */
  hasMeshTraffic: boolean;
}

// The log channels a process actually produced, from the continuum.stream ledger
// (processrun.channelNames is a calculated field: string_agg of that run's stream
// channels). This is the ground truth for "what can I view for this run" — a
// channel is listed here iff a stream was registered for it, whether it is still
// live in JetStream or already archived+reclaimed.
export async function fetchProcessRunViews(processUid: string): Promise<ProcessRunViews> {
  const rows = await sqlFirewallQuery(
    `SELECT channelNames FROM processrun WHERE uid = '${esc(processUid)}'`,
  );
  const raw = rows[0]?.channelNames ?? rows[0]?.channelnames;
  return { channels: parseLogChannels(raw), hasMeshTraffic: hasMeshTraffic(raw) };
}

export async function fetchProcessRunChannels(processUid: string): Promise<string[]> {
  return (await fetchProcessRunViews(processUid)).channels;
}

// The job's configured default log parser, if it set one. Backed by the firewall
// lens column `logparser` (@json_lens_str job.extra_data.logParser) — the same
// column the job form writes. Returns undefined when the job has no default (or
// stored a token we no longer know), so the caller falls through to the channel
// default. Never throws: a parser default is a nicety, not a reason to fail the
// view.
export async function fetchJobDefaultParser(
  jobUid: string,
): Promise<LogParserType | undefined> {
  if (!jobUid) return undefined;
  try {
    const rows = await sqlFirewallQuery(
      `SELECT logparser FROM job WHERE uid = '${esc(jobUid)}'`,
    );
    return parseLogParserType(rows[0]?.logparser as string | undefined);
  } catch {
    return undefined;
  }
}

// Fetch a JOB's recent process runs, within the last `days` days, newest first.
// Every run of the job is listed — we do NOT filter by kind: a job's runs may be
// bootstrap/observed/launchy-child depending on how it executes, so filtering to
// one kind hid all runs for jobs that don't use that topology.
//
// The subject is the JOB, and only the job. Two reasons, and the second is why
// glen settled the first (2026-09-09):
//   1. continuum groups runs by job now — the schedule/monitor/event a run came
//      from is not a subject the run picker offers.
//   2. job_uid is a PHYSICAL processrun column covered by the
//      (job_uid, startedat) index. scheduleUid is a calculated column backed by
//      jobevent, so keying on scheduleUid made the firewall join processrun to
//      jobevent through `processstart #>> '{eventUid}'` — an unindexed jsonb
//      expression — and postgres seq-scanned all ~814k processrun rows to
//      return one. Measured on continuum-prod 2026-09-09: 2222 ms by schedule,
//      3.6 ms by job, and the seq scan grew with the table.
// FULL_RUN_FIELDS still SELECTS the jobevent/job/schedule joins for the display
// label; that is fine, because the job_uid predicate drives the plan.
export async function fetchRecentProcessRuns(
  opts: { jobUid?: string | null; days?: number },
): Promise<ProcessRunRow[]> {
  if (!opts.jobUid) return [];
  const days = opts.days ?? 10;
  const since = new Date();
  since.setDate(since.getDate() - days);
  const sinceStr = since.toISOString();

  try {
    const rows = (await sqlFirewallQuery(
      `SELECT ${FULL_RUN_FIELDS} FROM processrun
        WHERE startedAt >= '${esc(sinceStr)}'
          AND job_uid = '${esc(opts.jobUid)}'`,
    )).map(normalizeRunRow);
    return rows.sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    );
  } catch {
    // Fallback: the firewall can't plan the jobevent/job joins (job.label,
    // scheduleUid, event.schedule.name all need them) for this query. Use RAW
    // columns only — never a join — and resolve the job name once directly.
    const rows = await sqlFirewallQuery(
      `SELECT ${RAW_RUN_FIELDS} FROM processrun
        WHERE startedat >= '${esc(sinceStr)}'
          AND job_uid = '${esc(opts.jobUid)}'`,
    );
    const jobName = await resolveJobName(opts.jobUid);
    return rows
      .map((r) => rawToRunRow(r, jobName))
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }
}
